import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';

/**
 * Notion/Asana-style stat card — colored icon chip + clean number.
 *
 *   <StatCard
 *     label="Total Projects"
 *     value={47}
 *     icon={<Assignment />}
 *     accent="#3b82f6"
 *   />
 */
const StatCard = ({ label, value, icon, accent = '#3b82f6', onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const interactive = typeof onClick === 'function';
  return (
    <Box
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
        borderRadius: 2.5,
        px: 3,
        py: 2.5,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          bottom: 0,
          background: accent,
          opacity: 0.9,
        },
        '&:hover': interactive
          ? {
              borderColor: alpha(accent, 0.4),
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 16px ${alpha(accent, 0.18)}`,
            }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mb: 1.25,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontSize: '1.625rem',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: alpha(accent, isDark ? 0.18 : 0.12),
              color: accent,
              '& svg': { fontSize: '1.5rem' },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatCard;
